<template>
    <header
        class="header"
        :class="classes"
    >
        <div class="header__title header-title">
            <div
                v-if="hasBeforeTitleContent"
                class="header-before-title-content"
            >
                <slot name="before-title-content" />
            </div>
            <div
                v-if="hasLeftSlot"
                class="header__slot header__slot--left"
            >
                <slot name="left-slot" />
            </div>
            <Tooltip
                id="header-tooltip"
                :title="title"
                :disabled="!withTooltip || !isTruncated"
                class="header-title__tooltip"
            >
                <Component
                    :is="titleTag"
                    :id="headerId"
                    ref="titleElement"
                    :data-cy="cypressId"
                    class="header-title__text"
                >
                    {{ title }}
                </Component>
            </Tooltip>
            <div
                v-if="hasAfterTitleContent"
                class="header-after-title-content"
            >
                <slot name="after-title-content" />
            </div>
        </div>

        <div
            v-if="isNavigationShown"
            class="header__navigation"
        >
            <slot name="navigation" />
        </div>

        <hr
            v-else-if="showDelimiter"
            class="header__delimiter"
        >

        <div
            v-if="hasRightSlot"
            class="header__slot header__slot--right"
        >
            <slot name="right-slot" />
        </div>
    </header>
</template>

<script lang="ts" setup>
import { computed, useSlots, useTemplateRef } from 'vue';
import { useTextTruncation } from '@/vue/common/composables/textTruncation';
import Tooltip from '../Elements/Tooltip.vue';
import HeaderVariant from './HeaderVariant';

type Props = {
    title?: string,
    headerId?: string|null,
    cypressId?: string|null,
    level?: number,
    variant?: string,
    bordered?: boolean,
    sticky?: boolean,
    leftAligned?: boolean,
    noHorizontalPadding?: boolean,
    withTooltip?: boolean,
    hideNavigation?: boolean,
};

const props = withDefaults(defineProps<Props>(), {
    title: 'Intelligent Video Solutions',
    level: 3,
    bordered: false,
    sticky: false,
    leftAligned: false,
    noHorizontalPadding: false,
    withTooltip: false,
    variant: HeaderVariant.DEFAULT,
    headerId: null,
    cypressId: null,
    hideNavigation: false,
});

const titleElement = useTemplateRef<HTMLElement>('titleElement');
const { isTruncated } = useTextTruncation(() => (props.withTooltip ? titleElement.value : null));

const titleTag = computed(() => `h${props.level}`);

const slots = useSlots();

const hasBeforeTitleContent = computed(() => !!slots['before-title-content']);
const hasAfterTitleContent = computed(() => !!slots['after-title-content']);
const hasLeftSlot = computed(() => !!slots['left-slot']);
const hasRightSlot = computed(() => !!slots['right-slot']);
const hasNavigation = computed(() => !!slots.navigation);

const isNavigationShown = computed(() => (
    hasNavigation.value && !props.hideNavigation
));

const showDelimiter = computed(() => props.variant === HeaderVariant.SECTION);

const classes = computed(() => ({
    [`header--${props.variant}`]: true,
    [`header--level-${props.level}`]: true,
    'header--no-navigation': !isNavigationShown.value,
    'header--bordered': props.bordered,
    'header--left-aligned': props.leftAligned,
    'header--no-horizontal-padding': props.noHorizontalPadding,
    'header--sticky': props.sticky,
}));
</script>

<style lang="scss" scoped>
@use '../../../styles/abstracts/variables';
@import '../../../styles/vendors/breakpoints';
@import '../../../styles/abstracts/spacings';
@import '../../../styles/abstracts/font-sizes';
@import '../../../styles/abstracts/mixins';

.header-title {
    display: flex;

    flex-direction: row;

    gap: $spacing-xs;

    align-items: center;

    min-width: 0;
    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;

    @include media-breakpoint-down(lg) {
        overflow: visible;
    }

    &__tooltip {
        min-width: 0;
    }

    &__text {
        @include text-ellipsis;

        margin: 0;
    }
}

.header {
    display: grid;

    flex-shrink: 0;

    grid-template-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-flow: column;

    gap: $spacing-s;

    align-items: stretch;
    justify-content: space-between;

    height: 4rem;
    padding-block: $spacing-s;
    padding-inline: $spacing-xl;

    &--level-3 {
        padding-inline: $spacing-l;
    }

    &--no-navigation {
        display: flex;

        flex-direction: row;

        @include media-breakpoint-down(lg) {
            padding-inline: $spacing-m;
        }
    }

    &--bordered {
        @include has-horizontal-rule($spacing-s);

        @include media-breakpoint-up(lg) {
            @include has-horizontal-rule();
        }
    }

    &--sticky {
        position: sticky;
        top: 0;
        z-index: 1;

        background-color: var(--theme-color-surface-overlay);
        backdrop-filter: blur(variables.$blur);
    }

    &--left-aligned {
        grid-template-columns: repeat(3, auto);

        justify-content: flex-start;
    }

    &--no-horizontal-padding {
        padding-inline: 0;
    }

    &__slot {
        &--left {
            width: 1.25rem;
        }

        &--right {
            display: flex;

            align-items: center;
            justify-content: flex-end;
        }
    }

    &__navigation {
        display: flex;

        align-items: center;

        justify-content: center;
    }

    &--section {
        gap: $spacing-xxl-2;
    }

    &__delimiter {
        flex-grow: 1;

        align-self: center;

        border: 0;
        border-bottom: 1px solid var(--theme-color-border-divider-secondary);
    }
}
</style>
